import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  TextField,
  IconButton,
  Paper,
  AppBar,
  Toolbar,
  Collapse,
  Divider,
  Grid,
  Link,
  Tabs,
  Tab,
  FormHelperText,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ListAltIcon from '@mui/icons-material/ListAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DeleteIcon from '@mui/icons-material/Delete'; // Add this import for DeleteIcon
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SettingsIcon from '@mui/icons-material/Settings';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FormLabel from '@mui/material/FormLabel';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuthContext } from '../context/AuthContext';
import { CaseInputType, UploadedFile, useCaseIntake } from '../context/CaseIntakeContext';
import { usePersistentNavigate } from '../utils/navigation';
import { UserType, Reasoning, Verbosity } from '../schema'; // NEW
import { useTranscribeMedical } from '../hooks/useTranscribeMedical';

// Add these type definitions at the top of your file, before your component
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
  error: any;
}

interface SpeechRecognitionResult {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResultList {
  [index: number]: SpeechRecognitionResult;
  length: number;
  item(index: number): SpeechRecognitionResult;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionEvent) => void;
  onend: () => void;
}

// Extend the Window interface to include the SpeechRecognition properties
declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition;
    webkitSpeechRecognition?: new () => SpeechRecognition;
  }
}

const ProviderLanding: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setCaseContextText, clearCaseContext, setCaseContextUploadedFiles, setCaseInputType, setContextReasoning, setContextVerbosity } = useCaseIntake();
  const { accessToken, logout, completedRegistration, userType, internalFlag } = useAuthContext();
  const navigate = usePersistentNavigate();
  const location = useLocation();
  const [footerExpanded, setFooterExpanded] = useState(true);
  const [showHeaderLogo, setShowHeaderLogo] = useState(false);
  const [intakeMethod, setIntakeMethod] = useState<CaseInputType | null>(null);
  const [caseText, setCaseText] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  // const [isRecording, setIsRecording] = useState(false); // Managed by hook now
  // const [recognition, setRecognition] = useState<SpeechRecognition | null>(null); // Removed
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reasoningLevel, setReasoningLevel] = useState<'Low' | 'Medium' | 'High'>('Low');
  const [outputVerbosity, setOutputVerbosity] = useState<'Low' | 'Medium' | 'High'>('Low');

  // Add this scroll restoration effect - it will run when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect admins to admin portal
  useEffect(() => {
    if (userType === UserType.ADMIN) {
      navigate('/admin');
    } else if (userType === UserType.REVIEWER) {
      navigate('/reviewer');
    }
  }, [userType, navigate]);

  // Animation state variables
  const [logoVisible, setLogoVisible] = useState(false);
  const [displayedFirstLine, setDisplayedFirstLine] = useState<string[]>([]);
  const [displayedSecondLine, setDisplayedSecondLine] = useState<string[]>([]);
  const [promptVisible, setPromptVisible] = useState(false);
  const [visibleTabs, setVisibleTabs] = useState<number[]>([]);

  const mainTitleRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Split subtitle into two lines
  const firstLine = 'Submit the details of a cancer case'.split(' ');
  const secondLine = 'to generate an expert second opinion'.split(' ');

  // Check scroll position to determine whether to show the header logo
  useEffect(() => {
    const handleScroll = () => {
      if (mainTitleRef.current) {
        const rect = mainTitleRef.current.getBoundingClientRect();
        // If the main title is not visible (scrolled up out of view)
        setShowHeaderLogo(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animation effects
  useEffect(() => {
    if (location.state?.rejectedText) {
      setCaseText(location.state.rejectedText);
      setIntakeMethod(CaseInputType.TEXTAREA);
    }
    if (location.state?.toastMessage) {
      setToastMessage(location.state.toastMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    // Step 1: Immediately load headers/footers (default behavior)

    // Step 2: Fade in the logo after a short delay
    const logoTimer = setTimeout(() => {
      setLogoVisible(true);

      // Step 3: Start typing animation word by word - first line
      let firstLineIndex = 0;
      const typingFirstLine = setInterval(() => {
        if (firstLineIndex <= firstLine.length) {
          setDisplayedFirstLine(firstLine.slice(0, firstLineIndex));
          firstLineIndex++;
        } else {
          clearInterval(typingFirstLine);

          // After first line completes, start second line
          let secondLineIndex = 0;
          const typingSecondLine = setInterval(() => {
            if (secondLineIndex <= secondLine.length) {
              setDisplayedSecondLine(secondLine.slice(0, secondLineIndex));
              secondLineIndex++;
            } else {
              clearInterval(typingSecondLine);

              // After both lines complete, fade in prompt and tabs
              setTimeout(() => {
                setPromptVisible(true);

                // Fade in tabs one by one
                setTimeout(() => setVisibleTabs([0]), 200);
                setTimeout(() => setVisibleTabs([0, 1]), 400);
                setTimeout(() => setVisibleTabs([0, 1, 2]), 600);
              }, 400);
            }
          }, 150); // 150ms between each word in second line
        }
      }, 150); // 150ms between each word in first line

      return () => clearInterval(typingFirstLine);
    }, 200);

    return () => clearTimeout(logoTimer);
  }, []);

  const toggleFooter = () => {
    setFooterExpanded(!footerExpanded);
  };

  // Add a function to stop recording (extracted for reuse)
  // const stopRecording = useCallback(() => { ... }); // Replaced by hook's stopTranscribe

  // Modify handleTabChange to stop recording when switching tabs
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Stop recording if active when changing tabs
    stopTranscribe();

    if (newValue === 0) {
      setIntakeMethod(CaseInputType.TEXTAREA);
    } else if (newValue === 1) {
      setIntakeMethod(CaseInputType.FORM);
    } else if (newValue === 2) {
      setIntakeMethod(CaseInputType.FILE);
    }
  };

  const handleSubmit = () => {
    const isAdmin = userType === UserType.ADMIN;
    if (isAdmin) {
      setToastMessage('Submitting cases is not yet supported for admin users.');
      return;
    }

    // Stop recording if active before submission
    stopTranscribe();

    // This would handle the submission based on the intake method
    if (intakeMethod === CaseInputType.TEXTAREA) {
      console.log('Submitting free text case:', caseText);
      setCaseContextText(caseText); // Update context with the case text
      setCaseInputType(CaseInputType.TEXTAREA);
    } else if (intakeMethod === CaseInputType.FORM) {
      throw new Error('This code should not be reachable!');
    } else {
      console.log('Submitting uploaded files:', uploadedFiles);
      setCaseContextUploadedFiles(uploadedFiles); // Update context with uploaded files
      setCaseInputType(CaseInputType.FILE);
    }

    // Map reasoning/verbosity
    const reasoningMap: Record<string, Reasoning> = {
      'Low': Reasoning.REASONING_LOW,
      'Medium': Reasoning.REASONING_MEDIUM,
      'High': Reasoning.REASONING_HIGH
    };
    const verbosityMap: Record<string, Verbosity> = {
      'Low': Verbosity.VERBOSITY_LOW,
      'Medium': Verbosity.VERBOSITY_MEDIUM,
      'High': Verbosity.VERBOSITY_HIGH
    };

    setContextReasoning(reasoningMap[reasoningLevel]);
    setContextVerbosity(verbosityMap[outputVerbosity]);

    if (accessToken == null) {
      navigate('/login');
      return;
    }

    // Only providers need to complete registration, not reviewers or admins
    const isReviewer = userType === UserType.REVIEWER;
    if (!completedRegistration && !isAdmin && !isReviewer) {
      navigate('/complete-registration');
      return;
    }

    sessionStorage.removeItem('lastSubmittedCaseResponse');
    navigate('/case-report');
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList: FileList) => {
    const newFiles = Array.from(fileList).map(file => ({
      file,
      preview: file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined
    }));

    setUploadedFiles([...uploadedFiles, ...newFiles]);
  };

  const removeFile = (index: number) => {
    const updatedFiles = [...uploadedFiles];
    // Revoke the object URL to avoid memory leaks
    if (updatedFiles[index].preview) {
      URL.revokeObjectURL(updatedFiles[index].preview as string);
    }    // ...existing code...
    <Typography
      variant="h5"
      sx={{
        mb: { xs: 4, sm: 4 },
        textAlign: 'center',
        color: '#4a5568',
        fontSize: { xs: '1.1rem', sm: '1.5rem' },
      }}
    >
      <Box component="span" sx={{ display: 'inline-block' }}>
        Submit the details of a cancer case
      </Box>
      {' '}
      <Box component="span" sx={{ display: 'inline-block' }}>
        to generate an expert second opinion
      </Box>
    </Typography>
    // ...existing code...
    updatedFiles.splice(index, 1);
    setUploadedFiles(updatedFiles);
  };

  // Clean up object URLs on component unmount
  // Use the new AWS Transcribe Medical hook
  const { startRecording: startTranscribe, stopRecording: stopTranscribe, isRecording, error: transcribeError } = useTranscribeMedical();

  // Clean up object URLs on component unmount
  useEffect(() => {
    return () => {
      // Clean up file previews
      uploadedFiles.forEach(file => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });
    };
  }, [uploadedFiles]);

  // Add this function to handle voice recognition
  const toggleVoiceRecording = async () => {
    if (isRecording) {
      stopTranscribe();
    } else {
      try {
        await startTranscribe((newText) => {
          if (textAreaRef.current) {
            setCaseText(prev => {
              const cursorPosition = textAreaRef.current?.selectionStart || prev.length;
              // Basic append at cursor - might need refinement for spacing
              return prev.substring(0, cursorPosition) + (prev.length > 0 && prev[cursorPosition - 1] !== ' ' ? ' ' : '') + newText + prev.substring(cursorPosition);
            });

            // Scroll to bottom
            setTimeout(() => {
              if (textAreaRef.current) {
                textAreaRef.current.scrollTop = textAreaRef.current.scrollHeight;
              }
            }, 0);
          } else {
            setCaseText(prev => prev + ' ' + newText);
          }
        });
      } catch (error) {
        console.error('Speech recognition error:', error);
        alert("There was a problem initializing speech recognition.");
      }
    }
  };

  // Effect to show errors
  useEffect(() => {
    if (transcribeError) {
      setToastMessage(`Transcription Error: ${transcribeError}`);
    }
  }, [transcribeError]);

  // Add menu handler functions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleLoginClick = () => {
    handleMenuClose();
    navigate('/login');
  };

  const handleSignupClick = () => {
    handleMenuClose();
    navigate('/register');
  };

  const handleLogout = async () => {
    await logout();
    clearCaseContext();
  };

  const handleEnterIntakeForm = () => {
    const isAdmin = userType === UserType.ADMIN;
    if (isAdmin) {
      setToastMessage('Submitting cases is not yet supported for admin users.');
      return;
    }
    navigate('/submit-case');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#ffffff',
      color: '#2c5282',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Dialog
        open={!!toastMessage}
        onClose={() => setToastMessage(null)}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        disablePortal
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            p: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid #e2e8f0',
            pb: 1.5,
            pt: 2,
            color: '#2c5282',
            fontWeight: 600,
            fontSize: { xs: '1.2rem', sm: '1.5rem' }
          }}
        >
          Submission Rejected
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Box sx={{
            pt: 2,
            mb: 1,
            fontWeight: 500,
            color: '#2d3748',
            fontSize: '1rem',
            '& ul': { pl: 2.5, mt: 1, mb: 1 },
            '& li': { mb: 0.5 },
            '& p': { mt: 0, mb: 1 }
          }}>
            <ReactMarkdown>{toastMessage || ''}</ReactMarkdown>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={() => setToastMessage(null)}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#4299e1',
              color: 'white',
              py: 1.2,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 500,
              borderRadius: '8px',
              boxShadow: '0 2px 5px rgba(66, 153, 225, 0.3)',
              '&:hover': {
                bgcolor: '#2c5282',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(44, 82, 130, 0.4)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advanced Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ color: '#2c5282', fontWeight: 600, borderBottom: '1px solid #e2e8f0', pb: 1 }}>
          Advanced Settings
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3, mt: 1 }}>
            <FormLabel component="legend" sx={{ mt: 2, mb: 1, fontWeight: 500, color: '#4a5568' }}>Reasoning Level</FormLabel>
            <ToggleButtonGroup
              value={reasoningLevel}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) setReasoningLevel(newValue);
              }}
              fullWidth
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  '&.Mui-selected': {
                    bgcolor: '#ebf8ff',
                    color: '#2b6cb0',
                    borderColor: '#4299e1',
                    '&:hover': {
                      bgcolor: '#bee3f8',
                    }
                  }
                }
              }}
            >
              <ToggleButton value="Low">Low</ToggleButton>
              <ToggleButton value="Medium">Medium</ToggleButton>
              <ToggleButton value="High">High</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500, color: '#4a5568' }}>Output Verbosity</FormLabel>
            <ToggleButtonGroup
              value={outputVerbosity}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) setOutputVerbosity(newValue);
              }}
              fullWidth
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  '&.Mui-selected': {
                    bgcolor: '#ebf8ff',
                    color: '#2b6cb0',
                    borderColor: '#4299e1',
                    '&:hover': {
                      bgcolor: '#bee3f8',
                    }
                  }
                }
              }}
            >
              <ToggleButton value="Low">Low</ToggleButton>
              <ToggleButton value="Medium">Medium</ToggleButton>
              <ToggleButton value="High">High</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSettingsOpen(false)}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#4299e1',
              '&:hover': { bgcolor: '#2c5282' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Top Navigation */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e6e6e6' }}>
        <Toolbar sx={{
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          px: { xs: 1, sm: 2 } // Smaller padding on mobile
        }}>
          {/* Only show logo when user has scrolled past the main title */}
          {showHeaderLogo ? (
            <Box
              component="a"
              href="/"
              sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
            >
              <Box
                component="img"
                src="/logo.jpg"
                alt="Precisca"
                sx={{
                  height: { xs: 28, sm: 36 },
                  width: 'auto',
                  display: 'block',
                  mr: 1
                }}
              />
            </Box>
          ) : (
            <Box /> // Empty box to maintain layout
          )}

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Desktop Navigation - only show Login/Signup when not logged in */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              {!accessToken ? (
                <>
                  <Button
                    variant="outlined"
                    sx={{
                      color: '#2c5282',
                      borderColor: '#2c5282',
                      mr: 2,
                      px: { xs: 2, sm: 3 },
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: '#4299e1',
                        color: '#4299e1'
                      }
                    }}
                    onClick={() => navigate('/login')}
                  >
                    Log In
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: '#4299e1',
                      color: 'white',
                      mr: 2,
                      px: { xs: 2, sm: 3 },
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      '&:hover': {
                        bgcolor: '#2c5282'
                      },
                      borderRadius: '4px'
                    }}
                    onClick={() => navigate('/register')}
                  >
                    Sign Up
                  </Button>
                </>
              ) : null}
            </Box>

            {/* Hamburger Menu for all users */}
            <IconButton
              color="inherit"
              sx={{
                color: '#2c5282',
              }}
              onClick={handleMenuOpen}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile/Desktop Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            mt: 1.5,
            minWidth: '150px',
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {!accessToken ? [
          <MenuItem
            key="login"
            onClick={handleLoginClick}
            sx={{
              py: 1.5,
              fontSize: '0.9rem',
              color: '#4a5568',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#f7fafc',
                color: '#4299e1',
              }
            }}
          >
            Log In
          </MenuItem>,
          <MenuItem
            key="signup"
            onClick={handleSignupClick}
            sx={{
              py: 1.5,
              fontSize: '0.9rem',
              color: '#4a5568',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#f7fafc',
                color: '#4299e1',
              }
            }}
          >
            Sign Up
          </MenuItem>
        ] : [
          <MenuItem
            key="home"
            onClick={() => {
              handleMenuClose();
              navigate('/');
            }}
            sx={{
              py: 1.5,
              fontSize: '0.9rem',
              color: '#4a5568',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#f7fafc',
                color: '#4299e1',
              }
            }}
          >
            Home
          </MenuItem>,
          ...(userType === UserType.ADMIN ? [
            <MenuItem
              key="admin"
              onClick={() => {
                handleMenuClose();
                navigate('/admin');
              }}
              sx={{
                py: 1.5,
                fontSize: '0.9rem',
                color: '#4a5568',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#f7fafc',
                  color: '#4299e1',
                }
              }}
            >
              Admin Portal
            </MenuItem>
          ] : []),
          ...(userType === UserType.PROVIDER ? [
            <MenuItem
              key="my-cases"
              onClick={() => {
                handleMenuClose();
                navigate('/my-cases');
              }}
              sx={{
                py: 1.5,
                fontSize: '0.9rem',
                color: '#4a5568',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#f7fafc',
                  color: '#4299e1',
                }
              }}
            >
              My Cases
            </MenuItem>
          ] : []),
          <MenuItem
            key="logout"
            onClick={() => {
              handleMenuClose();
              handleLogout();
            }}
            sx={{
              py: 1.5,
              fontSize: '0.9rem',
              color: '#4a5568',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#f7fafc',
                color: '#4299e1',
              }
            }}
          >
            Log Out
          </MenuItem>
        ]}
      </Menu>

      {/* Main Content */}
      <Box sx={{
        minHeight: '100vh',
        bgcolor: '#ffffff',
        color: '#2c5282',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Top Spacer - flexible space to help centering */}
        <Box sx={{ flex: 1 }} />

        <Container maxWidth="md" sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: { xs: 2, sm: 4 }, // Reduced vertical padding since spacers handle it
          px: { xs: 2, sm: 3 },
          position: 'relative',
          zIndex: 1
        }}>
          {/* Logo with fade-in animation */}
          <Box
            ref={mainTitleRef}
            sx={{
              mb: { xs: 3, sm: 3 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: logoVisible ? 1 : 0,
              transition: 'opacity 0.8s ease-in',
            }}
          >
            <Box
              component="img"
              src="/ai logo no background.png"
              alt="Precisca AI"
              sx={{
                height: { xs: 48, sm: 64 },
                width: 'auto',
                display: 'block'
              }}
            />
          </Box>

          {/* Call to action with word-by-word typing animation - two lines */}
          <Typography
            variant="h5"
            sx={{
              mb: { xs: 4, sm: 4 },
              textAlign: 'center',
              color: '#4a5568',
              fontSize: { xs: '1.1rem', sm: '1.5rem' },
              minHeight: '4rem', // Reserve space for two lines
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {displayedFirstLine.join(' ')}
            {displayedSecondLine.length > 0 && <Box component="span" sx={{ mt: 0.0 }}>{displayedSecondLine.join(' ')}</Box>}
          </Typography>

          {/* Intake selection prompt with fade-in */}
          {intakeMethod === null && (
            <Typography
              sx={{
                mb: { xs: 4, sm: 4 },
                textAlign: 'center',
                color: '#aaaaabff',
                fontSize: { xs: '0.7rem', sm: '0.9rem' },
                opacity: promptVisible ? 1 : 0,
                transition: 'opacity 0.6s ease-in',
              }}
            >
              Select one of our three supported intake methods to get started.
            </Typography>
          )}

          {/* Intake Method Selection with sequential tab reveal */}
          <Paper elevation={0} sx={{
            width: '100%',
            borderRadius: 2,
            mb: { xs: 5, sm: 4 },
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            <Tabs
              value={intakeMethod ? intakeMethod.valueOf() - 1 : false}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: '#4299e1',
                },
                '& .Mui-selected': {
                  color: '#2c5282',
                  fontWeight: 'bold'
                },
                '& .MuiTab-root': {
                  minHeight: { xs: '48px', sm: '64px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }
              }}
            >
              {/* FIXED: Proper tab label and icon fade-in */}
              <Tab
                icon={<TextFieldsIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                label="Manual Summary"
                iconPosition="start"
                sx={{
                  py: { xs: 1, sm: 2 },
                  opacity: visibleTabs.includes(0) ? 1 : 0,
                  transition: 'opacity 0.5s ease-in',
                }}
                value={0}
              />
              <Tab
                icon={<ListAltIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                label="Intake Form"
                iconPosition="start"
                sx={{
                  py: { xs: 1, sm: 2 },
                  opacity: visibleTabs.includes(1) ? 1 : 0,
                  transition: 'opacity 0.5s ease-in',
                }}
                value={1}
              />
              <Tab
                icon={<UploadFileIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                label={internalFlag ? 'Upload Records' : (isMobile ? 'Upload Records' : <>Upload Records<br />(Coming Soon)</>)}
                iconPosition="start"
                sx={{
                  py: { xs: 1, sm: 2 },
                  opacity: visibleTabs.includes(2) ? 1 : 0,
                  transition: 'opacity 0.5s ease-in',
                }}
                disabled={!internalFlag}
                value={2}
              />
            </Tabs>

            {/* Only render the Box with padding when a tab is selected */}
            {intakeMethod !== null && (
              <Box sx={{ p: { xs: 2, sm: 3 } }}> {/* Less padding on mobile */}
                {/* Manual Summary Input with Voice-to-Text */}
                {intakeMethod === CaseInputType.TEXTAREA && (
                  <Box>
                    {/* Temporarily hidden - HIPAA compliance warning
                    <Alert severity="info" sx={{ mb: 1 }}>
                      <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        This application is not yet HIPAA compliant. Do not include any PII.
                      </Typography>
                    </Alert>
                    */}

                    <Typography variant="body1" sx={{
                      mb: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' } // Smaller text size on mobile
                    }}>
                      You may click the microphone to dictate or directly type below to provide case details.
                    </Typography>

                    {/* For text area with voice button */}
                    <Box sx={{ position: 'relative', mb: { xs: 2, sm: 3 } }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={caseText}
                        onChange={(e) => setCaseText(e.target.value)}
                        placeholder="Type/dictate case details, e.g.: 71 y/o with 1.2 cm right breast mass, biopsy showing IDC grade 2-3, ER/PR negative, HER-2 IHC 0 (negative), no suspicious lymph nodes on imaging, etc..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#f7fafc',
                            fontSize: { xs: '0.875rem', sm: '1rem' } // Smaller text on mobile
                          }
                        }}
                        inputRef={textAreaRef}
                      />
                      {/* Position voice button - top right on mobile, bottom right on desktop */}
                      <IconButton
                        onClick={() => setSettingsOpen(true)}
                        sx={{
                          position: 'absolute',
                          right: { xs: 32, sm: 42 }, // Left of the mic button
                          top: 'auto',
                          bottom: { xs: 4, sm: 4 },
                          padding: { xs: '4px', sm: '8px' },
                          color: '#a0aec0', // Grey color
                          transform: 'none',
                          zIndex: 2,
                          '&:hover': {
                            color: '#4a5568',
                            bgcolor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <SettingsIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      </IconButton>

                      <IconButton
                        onClick={toggleVoiceRecording}
                        sx={{
                          position: 'absolute',
                          right: { xs: 4, sm: 4 },
                          top: 'auto',
                          bottom: { xs: 4, sm: 4 },
                          padding: { xs: '4px', sm: '8px' },
                          backgroundColor: isRecording ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                          color: isRecording ? '#ef4444' : '#4299e1',
                          transform: 'none',
                          zIndex: 2 // Ensure it's above the textarea content
                        }}
                      >
                        {isRecording ? <MicOffIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} /> : <MicIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />}
                      </IconButton>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      sx={{
                        bgcolor: '#4299e1',
                        '&:hover': { bgcolor: '#2c5282' },
                        height: '48px',
                        fontSize: '1rem'
                      }}
                      onClick={handleSubmit}
                      disabled={!caseText.trim()}
                    >
                      Submit Case
                    </Button>
                  </Box>
                )}

                {/* Intake Form */}
                {intakeMethod === CaseInputType.FORM && (
                  <Box>
                    <Typography variant="body1" sx={{
                      mb: 3,
                      fontSize: { xs: '0.875rem', sm: '1rem' } // Smaller text size on mobile
                    }}>
                      Our structured intake form will guide you through entering all relevant information about your case.
                    </Typography>

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      sx={{
                        bgcolor: '#4299e1',
                        '&:hover': { bgcolor: '#2c5282' },
                        height: '48px',
                        fontSize: '1rem'
                      }}
                      onClick={handleEnterIntakeForm}
                    >
                      Enter Intake Form
                    </Button>
                  </Box>
                )}

                {/* File Upload */}
                {intakeMethod === CaseInputType.FILE && (
                  <Box>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        IMPORTANT: This application is not yet HIPAA compliant
                      </Typography>
                      <Typography variant="body2">
                        Any submitted documents must be anonymized or scrubbed of PII prior to upload.
                        Consider using one of the other intake methods if you cannot ensure complete removal of PII.
                      </Typography>
                    </Alert>

                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: isDragging ? '#4299e1' : '#cbd5e0',
                        borderRadius: 2,
                        p: { xs: 1.5, sm: 2 }, // Less padding on mobile
                        mb: { xs: 1.5, sm: 2 },
                        backgroundColor: isDragging ? 'rgba(66, 153, 225, 0.05)' : '#f7fafc',
                        textAlign: 'center',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                      }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleFileDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        type="file"
                        multiple
                        hidden
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".pdf,.docx,.jpg,.jpeg,.png,.txt"
                      />
                      <UploadFileIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: '#4299e1', mb: 0 }} />
                      <Typography variant="h6" sx={{
                        mb: 0,
                        fontSize: { xs: '1rem', sm: '1.25rem' } // Smaller heading on mobile
                      }}>
                        Drag-and-drop or click to upload
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{
                        mt: 0,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' } // Smaller text on mobile
                      }}>
                        Supported formats: PDF, DOCX, JPEG, PNG, TXT
                      </Typography>
                    </Box>

                    {/* File List */}
                    {uploadedFiles.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Uploaded Files ({uploadedFiles.length})
                        </Typography>
                        <Paper variant="outlined" sx={{ maxHeight: '200px', overflow: 'auto' }}>
                          <List dense>
                            {uploadedFiles.map((file, index) => (
                              <ListItem
                                key={index}
                                secondaryAction={
                                  <IconButton edge="end" onClick={() => removeFile(index)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                }
                              >
                                <ListItemIcon sx={{ minWidth: '30px' }}>
                                  <FiberManualRecordIcon sx={{ fontSize: 8, color: '#4299e1' }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={file.file.name}
                                  secondary={`${(file.file.size / 1024).toFixed(1)} KB`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Box>
                    )}

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      sx={{
                        bgcolor: '#4299e1',
                        '&:hover': { bgcolor: '#2c5282' },
                        height: '48px',
                        fontSize: '1rem'
                      }}
                      onClick={handleSubmit}
                      disabled={uploadedFiles.length === 0}
                    >
                      Submit {uploadedFiles.length} {uploadedFiles.length === 1 ? 'File' : 'Files'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Container>

        {/* Bottom Spacer - heavier weight to push content higher up */}
        <Box sx={{ flex: 1.5 }} />
      </Box>

      {/* Main Footer */}
      <Box sx={{ bgcolor: '#f1f5f9', py: { xs: 4, sm: 6 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 3, sm: 5 }}>
            {/* Logo Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                component="img"
                src="/logo no background.png"
                alt="Precisca"
                sx={{
                  height: 32,
                  width: 'auto',
                  mb: 2,
                  display: 'block'
                }}
              />
              <Typography variant="body2" sx={{
                color: '#4a5568',
                mb: 2,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Elevating cancer care through <br></br> virtual second opinions.
              </Typography>
            </Grid>

            {/* Products Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{
                color: '#2c5282',
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Products
              </Typography>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                AI Reviews
              </Link>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Doctor Opinions
              </Link>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Video Library
              </Link>
              <Link href="/legal/PrecisCa.AI Terms and Conditions.pdf" underline="hover" sx={{
                display: 'block',
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Terms of Service
              </Link>
            </Grid>

            {/* Company Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{
                color: '#2c5282',
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Company
              </Typography>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                About
              </Link>
              <Link href="#" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Our Faculty
              </Link>
              <Link href="/legal/PrecisCa.AI Privacy Policy.pdf" underline="hover" sx={{
                display: 'block',
                mb: 1,
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Privacy Policy
              </Link>
              <Link href="/legal/PrecisCa.AI Disclaimer.pdf" underline="hover" sx={{
                display: 'block',
                color: '#4a5568',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Disclaimer
              </Link>
            </Grid>

            {/* Newsletter Column */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" sx={{
                color: '#2c5282',
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
                Stay Updated
              </Typography>
              <Typography variant="body2" sx={{
                color: '#4a5568',
                mb: 2,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Stay abreast of the latest PrecisCa developments and new products.
              </Typography>
              <Paper
                component="form"
                sx={{
                  display: 'flex',
                  p: '2px 4px',
                  alignItems: 'center',
                  width: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 1
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Email"
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      px: 1,
                      py: { xs: 0.3, sm: 0.5 },
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }
                  }}
                />
                <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                <Button
                  type="submit"
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 0.3, sm: 0.5 },
                    bgcolor: '#4299e1',
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&:hover': { bgcolor: '#2c5282' }
                  }}
                >
                  Subscribe
                </Button>
              </Paper>
            </Grid>
          </Grid>

          {/* Copyright Section */}
          <Box sx={{ mt: { xs: 4, sm: 6 }, pt: { xs: 2, sm: 3 }, borderTop: '1px solid #e2e8f0' }}>
            <Grid
              container
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'center', sm: 'center' }}
              spacing={{ xs: 2, sm: 0 }}
            >
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Typography variant="body2" sx={{
                  color: '#4a5568',
                  textAlign: { xs: 'center', sm: 'left' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                  © PrecisCa AI 2025. All rights reserved.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Box sx={{
                  display: 'flex',
                  gap: { xs: 2, sm: 3 },
                  justifyContent: { xs: 'center', sm: 'flex-start' }
                }}>
                  <Link href="/legal/PrecisCa.AI Disclaimer.pdf" underline="hover" sx={{
                    color: '#4a5568',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    Disclaimer
                  </Link>
                  <Link href="/legal/PrecisCa.AI Terms and Conditions.pdf" underline="hover" sx={{
                    color: '#4a5568',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    Terms of Use
                  </Link>
                  <Link href="/legal/PrecisCa.AI Privacy Policy.pdf" underline="hover" sx={{
                    color: '#4a5568',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    Privacy Policy
                  </Link>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Conditionally render the sticky bottom bar */}
      {!accessToken && (
        <>
          {/* Add padding to account for the height of the sticky footer */}
          <Box sx={{ pb: footerExpanded ? '70px' : '50px' }}></Box>

          {/* Sticky Collapsible Footer */}
          <Box sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Collapsed Footer with Toggle Button */}
            {!footerExpanded && (
              <Box sx={{
                bgcolor: '#f7fafc',
                borderTop: '1px solid #e6e6e6',
                py: { xs: 1, sm: 2 },
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
                onClick={toggleFooter}
              >
                <Typography variant="body2" sx={{
                  color: '#333333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                  Expand to Sign Up
                </Typography>
              </Box>
            )}

            {/* Expanded Footer Content */}
            <Collapse in={footerExpanded}>
              <Box sx={{
                bgcolor: '#374151',
                py: { xs: 1.5, sm: 2.6 },
                color: 'white'
              }}>
                <Container maxWidth="lg" sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: { xs: 0.5, sm: 0 }
                }}>
                  <Typography sx={{
                    color: 'white',
                    fontSize: { xs: '0.85rem', sm: '1.25rem' },
                    mb: { xs: 0.5, sm: 0 },
                    textAlign: { xs: 'center', sm: 'left' },
                    fontWeight: { xs: 'normal', sm: 500 }
                  }}>
                    PrecisCa AI opinions are currently only available for licensed healthcare professionals.{' '}
                    <Box
                      component="span"
                      sx={{
                        color: '#4299e1',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#63b3ed'
                        }
                      }}
                      onClick={() => navigate('/register')}
                    >
                      Click here
                    </Box> to register.
                  </Typography>
                  <IconButton
                    onClick={toggleFooter}
                    sx={{
                      color: 'white',
                      padding: { xs: '4px', sm: '8px' },
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                    aria-label="collapse footer"
                  >
                    <ExpandMoreIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                  </IconButton>
                </Container>
              </Box>
            </Collapse>
          </Box>
        </>
      )}

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default ProviderLanding;
